import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import type { ResolvedLocationResult } from '../pages/userSettingsApi'
import { SERIF_HEADING } from '../theme'

export type LocationDialogStage = 'permission' | 'resolving' | 'confirm' | 'manual'

export function LocationConsentDialog({
  open,
  stage,
  busy,
  error,
  reason,
  result,
  onFind,
  onConfirm,
  onManual,
  onDecline,
  onCancel,
}: {
  open: boolean
  stage: LocationDialogStage
  busy: boolean
  error: string | null
  reason?: string
  result: ResolvedLocationResult | null
  onFind: () => void
  onConfirm: () => void
  onManual: (value: string) => void
  onDecline: () => void
  onCancel: () => void
}) {
  const [manualLocation, setManualLocation] = useState('')
  useEffect(() => {
    if (!open) setManualLocation('')
  }, [open])

  const nearby = result?.nearbyPlaces.slice(0, 4).map(place => place.city)
    .filter((name, index, all) => all.indexOf(name) === index)

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(0,143,168,0.1)', color: 'primary.main', display: 'grid', placeItems: 'center', mb: 2 }}>
          {stage === 'resolving' ? <CircularProgress size={24} /> : <LocationOnOutlinedIcon />}
        </Box>

        {stage === 'permission' && (
          <>
            <Typography variant="h5" sx={{ ...SERIF_HEADING, mb: 1 }}>Use your location for this answer?</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.55 }}>
              {reason || 'Sharing your approximate location helps Ros give you a more relevant local answer.'}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 1.5, lineHeight: 1.45 }}>
              Ros uses your position to suggest a readable city or municipality. Nothing is saved until you review and confirm it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button disabled={busy} variant="contained" onClick={onFind} sx={{ textTransform: 'none', flex: 1 }}>Find my location</Button>
              <Button disabled={busy} variant="outlined" onClick={() => onManual('')} sx={{ textTransform: 'none' }}>Enter manually</Button>
            </Box>
            <Button disabled={busy} size="small" color="inherit" onClick={onDecline} sx={{ textTransform: 'none', mt: 1 }}>Not now</Button>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', mt: 1.5, lineHeight: 1.4 }}>
              If you choose not to share, Ros may use a less precise network location when available.
            </Typography>
          </>
        )}

        {stage === 'resolving' && (
          <>
            <Typography variant="h5" sx={{ ...SERIF_HEADING, mb: 1 }}>Ros is finding the right place</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.92rem', lineHeight: 1.55 }}>
              Comparing nearby places and identifying the city or municipality people would normally use.
            </Typography>
          </>
        )}

        {stage === 'confirm' && result && (
          <>
            <Typography variant="h5" sx={{ ...SERIF_HEADING, mb: 1 }}>Confirm your location</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>Ros found</Typography>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mt: 0.3 }}>{result.resolution.displayName}</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mt: 1, lineHeight: 1.45 }}>
              {result.resolution.explanation}
            </Typography>
            {!!nearby?.length && (
              <Typography sx={{ color: 'text.secondary', fontSize: '0.75rem', mt: 1.25 }}>
                Nearby evidence: {nearby.join(', ')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button disabled={busy} variant="contained" onClick={onConfirm} sx={{ textTransform: 'none', flex: 1 }}>
                {busy ? <CircularProgress size={18} color="inherit" /> : 'Yes, save this'}
              </Button>
              <Button disabled={busy} variant="outlined" onClick={() => onManual('')} sx={{ textTransform: 'none' }}>Correct it</Button>
            </Box>
            <Button disabled={busy} size="small" color="inherit" onClick={onFind} sx={{ textTransform: 'none', mt: 1 }}>Try again</Button>
          </>
        )}

        {stage === 'manual' && (
          <>
            <Typography variant="h5" sx={{ ...SERIF_HEADING, mb: 1 }}>Enter your location</Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: '0.86rem', mb: 2 }}>
              A city or municipality is enough. Ros will normalize it and ask you to confirm before saving.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={manualLocation}
              onChange={event => setManualLocation(event.target.value)}
              placeholder="e.g. Langley, BC"
              onKeyDown={event => {
                if (event.key === 'Enter' && manualLocation.trim() && !busy) onManual(manualLocation)
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
              <Button disabled={busy || !manualLocation.trim()} variant="contained" onClick={() => onManual(manualLocation)} sx={{ textTransform: 'none', flex: 1 }}>
                {busy ? <CircularProgress size={18} color="inherit" /> : 'Review location'}
              </Button>
              <Button disabled={busy} variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
            </Box>
          </>
        )}

        {error && <Typography role="alert" sx={{ color: 'error.main', fontSize: '0.82rem', mt: 1.5 }}>{error}</Typography>}
      </DialogContent>
    </Dialog>
  )
}
